import { formatShortcut, InputRichText, Tooltip } from '@dixa/lib-shared-habla';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { Channel } from 'lib-shared-types-channels';
import { useContext, useMemo, useRef } from 'react';
import { useUnmount } from 'react-use';

import { aiApi, ReadAiWritingImprovementArg } from 'apis/ai';
import { ConversationContext } from 'components/conversation';
import { copilotUtils } from 'utils/copilot';
import { useCopilotTrial } from 'utils/useCopilotTrial';
import { useEditorContext } from 'utils/useEditorContext';

import { PinnedActionProps } from '../copilotPinnedActions.types';

import { UIBlockerError } from './uiBlockerError';
import { UIBlockerLoading } from './uiBlockerLoading';

export const MatchToStyleGuide = ({
  channel,
  text,
}: PinnedActionProps): JSX.Element | null => {
  const { content } = useContext(InputRichText.Context);
  const { replace } = useEditorContext();

  const { hasCopilot } = useCopilotTrial();
  const triggerRef = useRef<ReturnType<typeof improveReply> | null>(null);

  const [improveReply, { status }] =
    aiApi.useCreateAiWritingImprovementMutation();

  const { conversation } = useContext(ConversationContext);

  const lastMessage = useMemo(() => {
    const events = conversation?.events || [];
    const messages = events.filter(
      (event) =>
        event.type === 'conversation:message_added' &&
        event.direction === 'inbound',
    );

    if (messages.length === 0) return '';
    if (!messages[messages.length - 1].text) return '';
    return messages[messages.length - 1].text as string;
  }, [conversation]);

  const { data: styleGuide } = aiApi.useReadAIWritingStyleGuideMatchQuery(
    {
      features: 'impr_reply',
      ...(channel && {
        channels: convertChannelCasing(channel, 'snake') as Channel,
      }),
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !channel,
    },
  );

  const hasStyleGuideForChannel = !!styleGuide?.data?.match;
  const { isDisabled, tooltipContent } =
    copilotUtils.matchToStyleGuide.getStatus({
      text,
      status,
      channel,
      hasStyleGuideForChannel,
    });

  const shortcut = formatShortcut([[['$metaOrCtrl', 'Shift'], 'KeyY']]);
  const tooltipWithShortcut = shortcut
    ? `${tooltipContent} (${shortcut})`
    : tooltipContent;

  useUnmount(() => {
    if (triggerRef.current) {
      triggerRef.current.abort();
    }
  });

  const improveReplyContext: ReadAiWritingImprovementArg['contexts'] = {
    features: 'impr_reply',
    ...(channel && {
      channels: convertChannelCasing(channel, 'snake') as Channel,
    }),
  };

  const performMatchToStyleGuide = async () => {
    content.setUiBlocker(
      <UIBlockerLoading loadingText={'Matching to style guide'} />,
    );

    const trigger = improveReply({
      lastMessage,
      text,
      promptName: 'repeat-input',
      contexts: improveReplyContext,
      csid: conversation?.id?.toString(),
    });
    triggerRef.current = trigger;

    trigger
      .unwrap()
      .then((response) => {
        triggerRef.current = null;
        replace(response.text);
        content.setUiBlocker(null);
      })
      .catch(() => {
        triggerRef.current = null;
        content.setUiBlocker(
          <UIBlockerError
            onClick={() => {
              content.setUiBlocker(null);
            }}
            errorText={'Failed to match to style guide'}
          />,
        );
      });
  };

  if (!hasCopilot) {
    return null;
  }

  return (
    <Tooltip content={tooltipWithShortcut}>
      <InputRichText.ToolbarButton
        icon="widget-customize"
        onClick={performMatchToStyleGuide}
        disabled={isDisabled}
      />
    </Tooltip>
  );
};
